import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { scaleLinear } from 'd3-scale';
import { format as d3Format } from 'd3-format';
import {
    ResponsiveContainer,
    BarChart,
    XAxis,
    YAxis,
    CartesianGrid,
    ReferenceLine,
    Tooltip,
    Bar,
    Cell,
} from 'recharts';
import { Box } from '@mui/material';
import DotWaveLoader from '../commonComponents/DotWaveLoader';
import { alpha, useTheme } from '@mui/material/styles';
import {
    ACTUAL_BAR_COLOR,
    OPTIMIZED_BAR_COLOR,
    getRiskOverviewChartChrome,
    getRiskOverviewTooltipStyle,
} from '../../utils/riskOverviewStyles';
import {
    getChartExtent,
    selectGroupChartRows,
} from '../../utils/riskOverviewChartPresentation';

const PERCENT_METRICS = new Set([
    'Factor % of Total Risk',
    'Idio % of Total Risk',
]);

const siFormatAxis = d3Format('.2~s');
function formatAbbrevAxis(value) {
    return siFormatAxis(value)
        .replace(/G$/, 'B')
        .replace(/k$/, 'T');
}

const siFormatTooltip = d3Format('.3~s');
function formatAbbrevTooltip(value) {
    return siFormatTooltip(value)
        .replace(/G$/, 'B')
        .replace(/k$/, 'T');
}

const fmtPercent = v => `${Math.round(v * 100)}%`;

function CustomTooltip({ active, payload, label, metricLabel, isPercentChart, theme, groupLabel }) {
    if (!active || !payload?.length) return null;
    const actual = payload.find(p => p.dataKey === 'value')?.value;
    const opt    = payload.find(p => p.dataKey === 'optValue')?.value;
    const hasActual = typeof actual === 'number';
    const hasOpt = typeof opt === 'number';
    let pctChange = '';
    if (hasActual && hasOpt && actual !== 0) {
        const raw  = ((opt - actual) / Math.abs(actual)) * 100;
        const sign = raw >= 0 ? '+' : '';
        pctChange   = ` (${sign}${raw.toFixed(2)}%)`;
    }
    return (
        <div style={getRiskOverviewTooltipStyle(theme)}>
            <strong>{metricLabel} – {label}</strong>
            <div style={{ color: ACTUAL_BAR_COLOR }}>
                {groupLabel}:&nbsp;
                {!hasActual
                    ? '—'
                    : isPercentChart
                    ? fmtPercent(actual)
                    : formatAbbrevTooltip(actual)}
            </div>
            {hasOpt && (
                <div style={{ color: OPTIMIZED_BAR_COLOR }}>
                    OPT {groupLabel}:&nbsp;
                    {isPercentChart
                        ? fmtPercent(opt)
                        : formatAbbrevTooltip(opt)}
                    {pctChange}
                </div>
            )}
        </div>
    );
}

function CustomizedYAxisTick({ x, y, payload, onGroupSelect, selectedGroup, theme, groupLabel }) {
    const raw = payload.value;
    const label = raw.length > 22 ? raw.slice(0, 22) + '...' : raw;
    const isSel = raw === selectedGroup;
    const chartChrome = getRiskOverviewChartChrome(theme);
    return (
        <text
            x={x - 8}
            y={y + 4}
            textAnchor="end"
            fill={isSel ? theme.palette.text.primary : chartChrome.tick}
            fontWeight={isSel ? 'bold' : 'normal'}
            style={{ cursor: 'pointer', fontSize: chartChrome.tickFontSize }}
            onClick={() => onGroupSelect(raw)}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onGroupSelect(raw);
                }
            }}
            role="button"
            tabIndex={0}
            aria-label={`Show ${raw} ${groupLabel.toLowerCase()} metrics`}
        >
            {label}
        </text>
    );
}

export default function GroupRiskMetricsBarChart({
    groupNames,
    data,
    optData = [],
    selectedGroup,
    metricLabel,
    onGroupSelect,
    sortKey,
    sortOrder,
    groupLabel,
    isLoading = false,
}) {
    const theme = useTheme();
    const chartChrome = getRiskOverviewChartChrome(theme);
    const { rows: chartData, hasOptimized: hasOpt } = useMemo(
        () => selectGroupChartRows({
            groupNames,
            actualRows: data,
            optimizedRows: optData,
            sortKey,
            sortOrder,
        }),
        [data, optData, groupNames, sortKey, sortOrder],
    );
    const { min: minVal, max: maxVal } = useMemo(
        () => getChartExtent(chartData, hasOpt),
        [chartData, hasOpt],
    );
    const isPercentChart = PERCENT_METRICS.has(metricLabel);
    const ticks = useMemo(
        () => scaleLinear().domain([minVal, maxVal]).nice(10).ticks(10),
        [minVal, maxVal],
    );

    if (isLoading) {
        return (
            <Box
                role="category"
                aria-label={`${metricLabel} by ${groupLabel.toLowerCase()}`}
                aria-busy="true"
                sx={{
                    width: '100%',
                    height: '100%',
                    minHeight: 360,
                    p: 2,
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                }}
            >
                <DotWaveLoader height={320} label={`Loading ${metricLabel.toLowerCase()} by ${groupLabel.toLowerCase()}`} />
            </Box>
        );
    }

    return (
        <Box
            role="category"
            aria-label={`${metricLabel} by ${groupLabel.toLowerCase()}`}
            sx={{ width: '100%', height: '100%', position: 'relative' }}
        >
            <ResponsiveContainer width="100%" height="100%">
                <BarChart
                    data={chartData}
                    layout="vertical"
                    margin={{ top: 0, right: 20, left: 20, bottom: 20 }}
                    barSize={20}
                    barCategoryGap={2}
                    barGap={-15}
                >
                    <CartesianGrid stroke={chartChrome.grid} strokeDasharray="3 3" horizontal={false} />
                    <XAxis
                        type="number"
                        domain={[minVal, maxVal]}
                        ticks={ticks}
                        tickFormatter={isPercentChart ? fmtPercent : formatAbbrevAxis}
                        tick={{ fill: chartChrome.tick, fontSize: chartChrome.tickFontSize }}
                        axisLine={{ stroke: chartChrome.axis }}
                        tickLine={{ stroke: chartChrome.axis }}
                    />
                    <ReferenceLine x={0} stroke={chartChrome.reference} />
                    <YAxis
                        dataKey="category"
                        type="category"
                        width={200}
                        tickLine={false}
                        axisLine={false}
                        tick={props => (
                            <CustomizedYAxisTick
                                {...props}
                                onGroupSelect={onGroupSelect}
                                selectedGroup={selectedGroup}
                                theme={theme}
                                groupLabel={groupLabel}
                            />
                        )}
                    />
                    <Tooltip
                        cursor={{
                            fill: chartChrome.cursor
                                || alpha(theme.palette.action.hover, theme.palette.mode === 'dark' ? 0.22 : 0.08),
                        }}
                        content={
                            <CustomTooltip
                                metricLabel={metricLabel}
                                isPercentChart={isPercentChart}
                                theme={theme}
                                groupLabel={groupLabel}
                            />
                        }
                    />
                    <Bar dataKey="value" fill={ACTUAL_BAR_COLOR} fillOpacity={0.3} isAnimationActive={false}>
                        {chartData.map(entry => (
                            <Cell
                                key={entry.category}
                                cursor="pointer"
                                onClick={() => onGroupSelect(entry.category)}
                            />
                        ))}
                    </Bar>
                    {hasOpt && (
                        <Bar
                            dataKey="optValue"
                            fill={OPTIMIZED_BAR_COLOR}
                            fillOpacity={0.4}
                            barSize={10}
                            isAnimationActive={false}
                        >
                            {chartData.map(entry => (
                                <Cell
                                    key={`opt-${entry.category}`}
                                    cursor="pointer"
                                    onClick={() => onGroupSelect(entry.category)}
                                />
                            ))}
                        </Bar>
                    )}
                </BarChart>
            </ResponsiveContainer>

        </Box>
    );
}

GroupRiskMetricsBarChart.propTypes = {
    groupNames:        PropTypes.arrayOf(PropTypes.string).isRequired,
    data:               PropTypes.array.isRequired,
    optData:            PropTypes.array,
    selectedGroup:     PropTypes.string.isRequired,
    metricLabel:        PropTypes.string.isRequired,
    onGroupSelect:     PropTypes.func.isRequired,
    sortKey:            PropTypes.string.isRequired,
    sortOrder:          PropTypes.string.isRequired,
    groupLabel:         PropTypes.string.isRequired,
    isLoading:          PropTypes.bool,
};
